<template>
  <el-container class="layout-wrap">
    <el-aside width="220px" class="layout-aside">
      <div class="logo">川旗中台</div>
      <el-menu
        :default-active="$route.path"
        class="menu"
        text-color="#c0c4cc"
        active-text-color="#409eff"
        background-color="#001529"
        router
      >
        <sidebar-menu :items="menuTree" />
      </el-menu>
    </el-aside>
    <el-container>
      <el-header class="layout-header">
        <div>{{ $route.meta.title || "管理后台" }}</div>
        <div class="right">
          <span class="username">{{ usernameText }}</span>
          <el-button size="mini" type="danger" @click="logout">退出登录</el-button>
        </div>
      </el-header>
      <el-main class="layout-main">
        <router-view />
      </el-main>
    </el-container>
  </el-container>
</template>

<script>
import { getMyMenuTree } from "../api/modules/menus";
import { refreshPermissionsFromServer } from "../utils/refreshPermissions";
import { clearAuth, getUser } from "../utils/storage";
import SidebarMenu from "./SidebarMenu.vue";
import { ROUTE_ALIAS_MAP, VALID_ROUTE_PATHS } from "../constants/menuRouteMap";

const KEYWORD_ROUTE_MAP = {
  user: "/users",
  users: "/users",
  dept: "/departments",
  department: "/departments",
  departments: "/departments",
  menu: "/menus",
  menus: "/menus",
  permission: "/permissions",
  permissions: "/permissions",
  "pay-config": "/wechat/pay-configs",
  "pay-configs": "/wechat/pay-configs",
  onboarding: "/wechat/onboardings",
  onboardings: "/wechat/onboardings",
  "mp-pay-orders": "/wechat/mp-pay-orders",
  "mp-pay-order": "/wechat/mp-pay-orders",
  apifactory: "/apifactory",
  "api-factory": "/apifactory",
  "cy-table": "/apifactory/table-query",
  "table-query": "/apifactory/table-query",
  table: "/apifactory/table-query",
  h5: "/h5/home",
  "h5-mobile": "/h5/home",
  "h5-home": "/h5/home",
  "h5-orders": "/h5/mp-pay-orders",
  "h5-mp-pay": "/h5/mp-pay-orders",
  "h5-onboardings": "/h5/onboardings",
  mobile: "/h5/home"
};

/** 后端未返回任何菜单节点时仅保留工作台（静态页），不再使用写死的全量菜单兜底。 */
const HOME_MENU_RAW = [
  {
    id: -1,
    name: "工作台",
    path: "/dashboard",
    component: null,
    visible: 1,
    status: 1,
    children: []
  }
];

export default {
  name: "AppLayout",
  components: {
    SidebarMenu
  },
  data() {
    return {
      currentUser: getUser(),
      menuTree: []
    };
  },
  mounted() {
    this.bootstrapLayout();
  },
  computed: {
    usernameText() {
      return this.currentUser && this.currentUser.username
        ? this.currentUser.username
        : "未登录";
    }
  },
  methods: {
    async bootstrapLayout() {
      try {
        await Promise.all([this.loadMenuTree(), refreshPermissionsFromServer()]);
      } catch (error) {
        await this.loadMenuTree();
      }
    },
    async loadMenuTree() {
      try {
        const data = await getMyMenuTree();
        if (!Array.isArray(data)) {
          this.menuTree = [];
          return;
        }
        const filtered = this.filterMenuTree(data);
        this.menuTree =
          filtered.length > 0 ? filtered : this.filterMenuTree(HOME_MENU_RAW);
      } catch (error) {
        this.menuTree = [];
        this.$message.warning("菜单加载失败，请刷新或联系管理员");
      }
    },
    filterMenuTree(items) {
      return (items || [])
        .filter((item) => item.visible !== 0 && item.status !== 0)
        .map((item) => this.mapMenuItem(item))
        .filter(Boolean);
    },
    mapMenuItem(item) {
      const children = this.filterMenuTree(item.children || []);
      const routePath = this.resolveRoutePath(item, children);

      // Skip pure container nodes with no path and no children.
      if (!routePath && children.length === 0) {
        return null;
      }

      return {
        ...item,
        routePath: routePath || `/menus?id=${item.id || ""}`,
        children
      };
    },
    mapPathToRoute(rawPath) {
      const normalized = this.normalizeRawPath(rawPath);
      if (!normalized) {
        return null;
      }
      const alias = ROUTE_ALIAS_MAP[normalized] || normalized;
      if (VALID_ROUTE_PATHS.has(alias)) {
        return alias;
      }
      const matched = this.matchByKeyword(normalized);
      if (matched) {
        return matched;
      }
      return null;
    },
    resolveRoutePath(item, children) {
      const byPath = this.mapPathToRoute(item.path);
      if (byPath) {
        return byPath;
      }
      const byComponent = this.mapPathToRoute(item.component);
      if (byComponent) {
        return byComponent;
      }
      const byName = this.matchByKeyword(item.name);
      if (byName) {
        return byName;
      }
      if (children.length > 0) {
        return children[0].routePath;
      }
      return null;
    },
    matchByKeyword(rawValue) {
      const normalized = this.normalizeRawPath(rawValue);
      if (!normalized) {
        return null;
      }
      const segments = normalized.split("/").filter(Boolean);
      for (let i = segments.length - 1; i >= 0; i -= 1) {
        const key = segments[i].toLowerCase();
        if (KEYWORD_ROUTE_MAP[key]) {
          return KEYWORD_ROUTE_MAP[key];
        }
      }
      return null;
    },
    normalizeRawPath(rawPath) {
      const value = String(rawPath || "").trim();
      if (!value) {
        return "";
      }
      const path = value.startsWith("/") ? value : `/${value}`;
      return path.replace(/\/{2,}/g, "/").replace(/\/$/, "") || "/";
    },
    logout() {
      clearAuth();
      this.$router.push("/login");
    }
  }
};
</script>

<style scoped>
.layout-wrap {
  min-height: 100vh;
}

.layout-aside {
  background: #001529;
}

.logo {
  height: 56px;
  line-height: 56px;
  text-align: center;
  color: #fff;
  font-weight: 600;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.menu {
  border-right: none;
}

.layout-header {
  background: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ebeef5;
}

.right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.username {
  color: #606266;
}

.layout-main {
  padding: 16px;
}
</style>
